import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Formulario, FormularioCampo } from "@/types/formulario";

export function useFormularioDistribuido(formularioId: string | undefined, notificacaoId: string | undefined) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['formulario-distribuido', formularioId, notificacaoId],
    queryFn: async () => {
      if (!formularioId || !notificacaoId) throw new Error('Parâmetros inválidos');

      // Verificar se o usuário tem acesso a esta notificação
      const { data: notificacao, error: notifError } = await supabase
        .from('formulario_notificacoes')
        .select('*')
        .eq('id', notificacaoId)
        .eq('usuario_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (notifError || !notificacao) {
        throw new Error('Você não tem permissão para acessar este formulário');
      }

      // Buscar formulário
      const { data: formulario, error: formError } = await supabase
        .from('formularios')
        .select(`
          *,
          clients(id, name)
        `)
        .eq('id', formularioId)
        .eq('status', 'ativo')
        .single();

      if (formError) throw formError;
      if (!formulario) throw new Error('Formulário não encontrado');

      // Buscar campos do formulário
      const { data: campos, error: camposError } = await supabase
        .from('formulario_campos')
        .select('*')
        .eq('formulario_id', formulario.id)
        .eq('ativo', true)
        .order('ordem', { ascending: true });

      if (camposError) throw camposError;

      return {
        formulario: formulario as Formulario,
        campos: (campos || []) as FormularioCampo[],
      };
    },
    enabled: !!formularioId && !!notificacaoId,
    retry: false,
  });

  return {
    formulario: data?.formulario,
    campos: data?.campos || [],
    isLoading,
    error: error as Error | null,
  };
}
